import { Injectable } from '@angular/core';
import { OsName } from '@shared/enums/os-info.enum';
import { OSInfo } from '@shared/models/os-info.model';

@Injectable({ providedIn: 'root' })
export class OsInfoService {
  getOSInfo(): OSInfo {
    const ua = navigator.userAgent;
    const platform = navigator.platform || '';
    const isMobile = this.isMobile(ua);

    return (
      this.detectWindows(ua, platform, isMobile) ??
      this.detectMacOS(ua, platform, isMobile) ??
      this.detectIOS(ua, isMobile) ??
      this.detectAndroid(ua, isMobile) ??
      this.detectLinux(ua, platform, isMobile) ??
      this.buildUnknown(ua, isMobile)
    );
  }

  // --------------------
  // OS Detectors
  // --------------------

  private detectWindows(
    ua: string,
    platform: string,
    isMobile: boolean,
  ): OSInfo | null {
    if (!ua.includes('Windows NT')) return null;

    const base = {
      os: OsName.Windows,
      version: this.getWindowsVersion(ua),
      isMobile,
      rawUA: ua,
    };

    return { ...base, label: this.buildLabel(base) };
  }

  private detectMacOS(
    ua: string,
    platform: string,
    isMobile: boolean,
  ): OSInfo | null {
    if (!ua.includes('Mac OS X') || isMobile) return null;

    const base = {
      os: OsName.MacOS,
      version: this.getMacVersion(ua),
      isMobile,
      rawUA: ua,
    };

    return { ...base, label: this.buildLabel(base) };
  }

  private detectIOS(ua: string, isMobile: boolean): OSInfo | null {
    if (!/iPhone|iPad|iPod/.test(ua)) return null;

    const base = {
      os: OsName.IOS,
      version: this.getIOSVersion(ua),
      isMobile,
      rawUA: ua,
    };

    return { ...base, label: this.buildLabel(base) };
  }

  private detectAndroid(ua: string, isMobile: boolean): OSInfo | null {
    if (!ua.includes('Android')) return null;

    const base = {
      os: OsName.Android,
      version: this.getAndroidVersion(ua),
      isMobile,
      rawUA: ua,
    };

    return { ...base, label: this.buildLabel(base) };
  }

  private detectLinux(
    ua: string,
    platform: string,
    isMobile: boolean,
  ): OSInfo | null {
    if (!ua.includes('Linux')) return null;

    const base = {
      os: OsName.Linux,
      isMobile,
      rawUA: ua,
    };

    return { ...base, label: this.buildLabel(base) };
  }

  private buildUnknown(ua: string, isMobile: boolean): OSInfo {
    const base = {
      os: OsName.Unknown,
      isMobile,
      rawUA: ua,
    };

    return { ...base, label: this.buildLabel(base) };
  }

  // --------------------
  // Helpers
  // --------------------

  private isMobile(ua: string): boolean {
    return /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
  }

  private buildLabel(info: {
    os: OsName;
    version?: string;
    isMobile: boolean;
  }): string {
    const parts: string[] = [];

    parts.push(info.os);

    if (info.version) {
      parts.push(info.version);
    }

    parts.push(info.isMobile ? '(mobile)' : '(desktop)');

    return parts.join(' ');
  }

  private getWindowsVersion(ua: string): string {
    if (ua.includes('Windows NT 10.0')) return '10 / 11';
    if (ua.includes('Windows NT 6.3')) return '8.1';
    if (ua.includes('Windows NT 6.2')) return '8';
    if (ua.includes('Windows NT 6.1')) return '7';
    return 'Unknown';
  }

  private getMacVersion(ua: string): string {
    // eslint-disable-next-line no-useless-escape
    const match = ua.match(/Mac OS X (\d+[_\.]\d+[_\.]?\d*)/);
    return match ? match[1].replace(/_/g, '.') : 'Unknown';
  }

  private getIOSVersion(ua: string): string {
    const match = ua.match(/OS (\d+[_\d]*) like Mac OS X/);
    return match ? match[1].replace(/_/g, '.') : 'Unknown';
  }

  private getAndroidVersion(ua: string): string {
    const match = ua.match(/Android (\d+(\.\d+)?)/);
    return match ? match[1] : 'Unknown';
  }
}
